/**
 * Single source of truth for per-table DataTable column widths (px).
 *
 * Every table that renders through the shared DataTable component defines
 * its column widths here as one `as const` object, keyed by camelCase names
 * after the column header/accessor. Column defs consume these via
 * `size: SOME_TABLE_COLUMN_WIDTHS.someColumn` instead of inline literals or
 * scattered per-file constants — this is the only place widths should be
 * declared or changed.
 *
 * Shared DataTable internals (e.g. the actions column width, default column
 * size) live in `DataTable/constants.ts` instead — those aren't per-table.
 *
 * Each table must leave at least one column unsized (no entry here, no `size:`
 * in its column def) so it absorbs surplus container width; if every column is
 * sized and their sum is narrower than the container, `table-fixed` stretches
 * ALL columns proportionally and none of these widths hold.
 */

import { SCROLLBAR_LANE_SIZE } from '@/react/components/ui/designed-scroll-area';
import { ACTIONS_COLUMN_WIDTH } from './constants';

/** Actions column for the tables that run a narrower kebab column than the
 *  shared ACTIONS_COLUMN_WIDTH: the 18px kebab trigger with 18px cell padding
 *  either side, plus the overlay scrollbar lane that the last cell reserves as
 *  a gutter (LAST_CELL_GUTTER_CLASSES in DataTable/constants.ts). */
const NARROW_ACTIONS_WIDTH = 18 * 3 + SCROLLBAR_LANE_SIZE;

/** Sales order table (SalesOrderTable/columns.tsx + its Storybook mock). */
export const SALES_ORDER_COLUMN_WIDTHS = {
  orderId: 140,
  events: 300,
  customer: 340,
  salesperson: 180,
  orderValue: 160,
  status: 150,
  xeroInvoice: 150,
} as const;

/** Events table (EventManagement/pages/AllEvents/columns.tsx). */
export const EVENT_COLUMN_WIDTHS = {
  select: 50,
  ids: 140,
  // Nominal design width for the three-row chip layout
  eventDetails: 399,
  deliveryDetails: 360,
  salesperson: 180,
  // EventStatusBadge is a fixed 123px pill; 123 + 2×18px cell padding
  status: 160,
  specialists: 200,
} as const;

/** Scheduler table (Scheduler/columns.tsx — special instructions flexes). */
export const SCHEDULER_COLUMN_WIDTHS = {
  select: 48,
  ids: 159,
  eventDetails: 360,
  address: 153,
  postal: 90,
  guest: 90,
  assignees: 267,
  drivers: 267,
  equipment: 180,
  locationNotes: 267,
  remarks: 267,
  actions: ACTIONS_COLUMN_WIDTH,
} as const;

/** Room kept for the unsized special instructions column once the scheduler table scrolls. */
export const SCHEDULER_FLEX_COLUMN_MIN_WIDTH = 359;

export const SCHEDULER_TABLE_MIN_WIDTH = Object.values(SCHEDULER_COLUMN_WIDTHS).reduce<number>(
  (total, width) => total + width,
  SCHEDULER_FLEX_COLUMN_MIN_WIDTH,
);

/** Price modifiers table (name column flexes). */
export const PRICE_MODIFIER_COLUMN_WIDTHS = {
  amount: 160,
  owner: 220,
} as const;

/** User table (UserManagement/index.tsx — name grows to a cap, then email flexes). */
export const USER_COLUMN_WIDTHS = {
  email: 280,
  mobile: 180,
  // The tag is one nowrap line; the longest label measures 133px, and 2×18px
  // cell padding puts the floor at 169.
  role: 180,
  actions: NARROW_ACTIONS_WIDTH,
} as const;

export const USER_NAME_COLUMN_WIDTHS = { min: 280, max: 420 } as const;

export const USER_TABLE_MIN_WIDTH = Object.values(USER_COLUMN_WIDTHS).reduce<number>(
  (total, width) => total + width,
  USER_NAME_COLUMN_WIDTHS.min,
);

/** Inventory table. Also the source for the Inventory loading skeleton. */
export const INVENTORY_COLUMN_WIDTHS = {
  name: 422,
  location: 164,
  retailPrice: 188,
  costPrice: 188,
  defaultConfiguration: 400,
} as const;

/** Resale item table. */
export const RESALE_ITEM_COLUMN_WIDTHS = {
  name: 360,
  vendor: 200,
  retailPrice: 188,
  costPrice: 188,
  itemNotes: 320,
} as const;

/** Closed hours management table. */
export const CLOSED_HOURS_COLUMN_WIDTHS = {
  start: 280,
  end: 280,
  reason: 400,
} as const;

/** Order source table. */
export const ORDER_SOURCE_COLUMN_WIDTHS = {
  name: 320,
  feeType: 160,
  fee: 120,
  description: 560,
} as const;

/** Preset menu management table (owner column flexes). */
export const PRESET_MENU_COLUMN_WIDTHS = {
  name: 300,
  format: 180,
  productLine: 210,
  pricePerPax: 153,
} as const;

/** Contacts table. */
export const CONTACT_COLUMN_WIDTHS = {
  name: 280,
  organisation: 240,
  email: 280,
  mobile: 180,
} as const;

/** Organisations table. GRAIN-1797 round-trip columns + Notes (GRAIN-1809). */
export const ORGANISATION_COLUMN_WIDTHS = {
  name: 280,
  sector: 200,
  tier: 160,
  domain: 220,
  notes: 240,
  contacts: 120,
} as const;

/** Locations table. */
export const LOCATION_COLUMN_WIDTHS = {
  postalCode: 120,
  notes: 639,
  configurations: 400,
} as const;

/**
 * Product table (Product/columns.tsx — shared by buffet/meal box variants).
 * Figma spec: node 8486-82447. `data` is reused across every plain-text/badge
 * column (category, catalogue, status, prices, macros, etc.) that shares the
 * same width by design, not per-column bespoke values.
 */
export const PRODUCT_COLUMN_WIDTHS = {
  select: 48,
  product: 399,
  data: 147,
  wide: 399,
  activePeriod: 180,
  // ProductStatusPill is a fixed 123px pill; 123 + 2×18px cell padding
  status: 160,
  stocks: 160,
  equipment: 500,
} as const;
